import React, { useEffect, useState, useRef } from "react";
import { Bell } from "lucide-react";
import "./TasksCalendar.css";

const API_BASE = process.env.REACT_APP_API_BASE || `${process.env.REACT_APP_API_URL}`;

/* ---------- Helper: API headers ---------- */
function apiHeaders(json = true) {
  const headers = {};
  if (json) headers["Content-Type"] = "application/json";
  const token = localStorage.getItem("token");
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

/* ---------- Helper: Safe fetch ---------- */
async function safeFetchJson(url, opts = {}) {
  try {
    const res = await fetch(url, opts);
    const ct = (res.headers.get("content-type") || "").toLowerCase();

    if (!res.ok) {
      if (ct.includes("application/json")) {
        const json = await res.json().catch(() => null);
        return { ok: false, error: json?.error || "Request failed" };
      } else {
        const txt = await res.text().catch(() => "");
        return { ok: false, error: `Server returned ${res.status}: ${txt}` };
      }
    }

    if (ct.includes("application/json")) {
      const json = await res.json().catch(() => null);
      return { ok: true, data: json };
    }
    return { ok: false, error: "Unexpected content type" };
  } catch (err) {
    return { ok: false, error: err.message };
  }
}

/* ---------- Date helpers ---------- */
function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function daysInMonth(d) {
  return endOfMonth(d).getDate();
}
function monthLabel(d) {
  return d.toLocaleString(undefined, { month: "long", year: "numeric" });
}
function ymd(date) {
  if (!date) return null;
  const dt = new Date(date);
  if (Number.isNaN(dt.getTime())) return null;
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/* ---------- Decode employee ID from JWT ---------- */
function getEmployeeIdFromToken() {
  const token = localStorage.getItem("token");
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    const payload = JSON.parse(atob(parts[1]));
    const candidates = [
      payload.employee_id,
      payload.employeeId,
      payload.empId,
      payload.employee?.id,
      payload.sub,
      payload.userId,
      payload.id,
    ];
    for (const c of candidates) {
      if (c != null && !Number.isNaN(Number(c))) return Number(c);
    }
  } catch {
    return null;
  }
  return null;
}

/* ---------- Main Component ---------- */
export default function TasksCalendar() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [selectedTask, setSelectedTask] = useState(null);
  const mountedRef = useRef(true);

  const currentEmployeeId = getEmployeeIdFromToken();

  useEffect(() => {
    mountedRef.current = true;
    loadTasks();
    return () => {
      mountedRef.current = false;
    };
  }, []);

  async function loadTasks() {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token provided. Please log in.");

      const res = await safeFetchJson(`${API_BASE}/api/tasks/my`, {
        headers: apiHeaders(false),
      });
      if (!res.ok) throw new Error(res.error);

      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      if (!mountedRef.current) return;

      const normalized = data
        .map((t) => {
          const filtered = currentEmployeeId
            ? (t.assignments || []).filter(
                (a) => Number(a.employee_id) === Number(currentEmployeeId)
              )
            : t.assignments || [];
          return {
            id: t.id,
            title: t.title || `Task #${t.id}`,
            description: t.description || "",
            due_date: t.due_date,
            status: t.status || "todo",
            milestone_title: t.milestone_title,
            project_name: t.project_name,
            assignments: filtered,
          };
        })
        .filter((t) => !currentEmployeeId || t.assignments?.length > 0);

      setTasks(normalized);
    } catch (err) {
      if (mountedRef.current) setError(err.message);
      setTasks([]);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }

  function prevMonth() {
    setMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  }
  function nextMonth() {
    setMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1));
  }
  function refresh() {
    loadTasks();
  }

  /* ---------- Calendar layout ---------- */
  const first = startOfMonth(month);
  const totalDays = daysInMonth(month);
  const startWeekday = first.getDay();
  const weeks = [];
  let curDay = 1 - startWeekday;
  while (curDay <= totalDays) {
    const week = [];
    for (let i = 0; i < 7; i++, curDay++) {
      if (curDay < 1 || curDay > totalDays) week.push(null);
      else week.push(new Date(month.getFullYear(), month.getMonth(), curDay));
    }
    weeks.push(week);
  }

  const tasksByDate = {};
  for (const t of tasks) {
    const key = ymd(t.due_date);
    if (!key) continue;
    if (!tasksByDate[key]) tasksByDate[key] = [];
    tasksByDate[key].push(t);
  }

  return (
    <div className="tcx-page">
      <header className="tcx-header">
        <div>
          <h2 className="tcx-title">My Task Reminders</h2>
          <p className="tcx-subtitle">
            Monthly overview of your assigned tasks
          </p>
        </div>

        <div className="tcx-controls">
          <button className="tcx-btn" onClick={refresh} disabled={loading}>
            Refresh
          </button>
          <button className="tcx-icon-btn" onClick={prevMonth}>
            ◀
          </button>
          <div className="tcx-month">{monthLabel(month)}</div>
          <button className="tcx-icon-btn" onClick={nextMonth}>
            ▶
          </button>

          {/* <button className="tcx-bell-btn" title="Notifications">
            <Bell size={22} color="white" fill="white" strokeWidth={1.8} />
            <span className="tcx-bell-badge">3</span>
          </button> */}
        </div>
      </header>

      {loading ? (
        <div className="tcx-message">Loading…</div>
      ) : error ? (
        <div className="tcx-error">Error: {error}</div>
      ) : (
        <div className="tcx-calendar">
          <div className="tcx-weekdays">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div key={d}>{d}</div>
            ))}
          </div>

          <div className="tcx-weeks">
            {weeks.map((week, wi) => (
              <div className="tcx-week" key={wi}>
                {week.map((d, di) => {
                  if (!d) return <div className="tcx-day-empty" key={di} />;
                  const key = ymd(d);
                  const dayTasks = tasksByDate[key] || [];
                  const isToday = ymd(d) === ymd(new Date());
                  return (
                    <div
                      className={`tcx-day ${isToday ? "today" : ""}`}
                      key={di}
                    >
                      <div className="tcx-day-num">{d.getDate()}</div>
                      <div className="tcx-day-tasks">
                        {dayTasks.slice(0, 3).map((t) => (
                          <button
                            key={t.id}
                            className="tcx-task-btn"
                            onClick={() => setSelectedTask(t)}
                          >
                            <span className="tcx-task-title">{t.title}</span>
                            {t.project_name && (
                              <small className="tcx-task-proj">
                                {t.project_name}
                              </small>
                            )}
                          </button>
                        ))}
                        {dayTasks.length > 3 && (
                          <div className="tcx-more">
                            +{dayTasks.length - 3} more
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedTask && (
        <div
          className="tcx-modal-overlay"
          onClick={() => setSelectedTask(null)}
        >
          <div className="tcx-modal" onClick={(e) => e.stopPropagation()}>
            <header className="tcx-modal-header">
              <h3>{selectedTask.title}</h3>
              <div className="tcx-modal-meta">
                <span>{selectedTask.project_name || "—"}</span>
                <span>
                  {selectedTask.milestone_title
                    ? `• ${selectedTask.milestone_title}`
                    : ""}
                </span>
                <span>
                  {selectedTask.due_date
                    ? new Date(selectedTask.due_date).toLocaleDateString()
                    : ""}
                </span>
              </div>
            </header>
            <div className="tcx-modal-body">
              <p>
                <strong>Status:</strong> {selectedTask.status}
              </p>
              <p>
                <strong>Description:</strong>
              </p>
              <div className="tcx-desc">{selectedTask.description || "—"}</div>
            </div>
            <footer className="tcx-modal-footer">
              <button className="tcx-btn" onClick={() => setSelectedTask(null)}>
                Close
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
